import { LitElement, PropertyValues, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { provide } from "@lit/context";
import {
  envContext,
  authContext,
  AuthState,
  renderSpecContext,
} from "@/context";
import { EnvConfig, RenderSpec } from "@/types";
import { initTheme } from "@/utils";
import { Auth } from "@saasbase-io/loginflow-websdk";

@customElement("sb-provider")
export class SbProvider extends LitElement {
  createRenderRoot() {
    return this;
  }

  /**
   * ENV now comes from a property instead of getEnvConfig()
   */
  @property({ type: Object })
  @provide({ context: envContext })
  env: EnvConfig | null = null;

  /**
   * Auth state
   */
  @state()
  @provide({ context: authContext })
  auth: AuthState = { isAuthenticated: false };

  @state()
  @provide({ context: renderSpecContext })
  renderSpec: string | null = null;

  /**
   * Enable/disable automatic redirect for authenticated users on auth pages.
   */
  @property({ type: Boolean })
  autoRedirect: boolean = true;

  connectedCallback(): void {
    super.connectedCallback();
    initTheme();

    const waitForEnv = async () => {
      while (!this.env || !this.env.domain) {
        await new Promise((res) => setTimeout(res, 20));
      }

      Auth.configure({
        domain: this.env.domain,
        appId: this.env.appId,
        clientSecret: this.env.clientSecret ?? "",
        locale: this.env.locale,
        signinUrl: this.env.signinUrl,
        signupUrl: this.env.signupUrl,
        onFlowStateChange: (event) => {
          if (event.render_spec) {
            this.renderSpec = event.render_spec;
          }
        },
      });

      this.refreshAuthState();
    };

    waitForEnv().catch((err) =>
      console.error("Error initializing SbProvider:", err),
    );
  }

  updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);

    if (changedProperties.has("env")) {
      // Reconfigure SDK if env changes
      if (this.env?.domain) {
        Auth.configure({
          domain: this.env.domain,
          appId: this.env.appId,
          clientSecret: this.env.clientSecret ?? "",
          locale: this.env.locale,
          signinUrl: this.env.signinUrl,
          signupUrl: this.env.signupUrl,
          onFlowStateChange: (event) => {
            if (event.render_spec) {
              this.renderSpec = event.render_spec;
            }
          },
        });
      }
    }

    if (changedProperties.has("auth")) {
      this.handleAuthStateChange();
    }
  }

  private handleAuthStateChange(): void {
    if (!this.autoRedirect || !this.auth.isAuthenticated) return;
    if (!this.env) return;

    const currentPath = window.location.pathname;
    const authPageUrls: string[] = [this.env.signinUrl, this.env.signupUrl];
    const isOnAuthPage = authPageUrls.some((authUrl) =>
      currentPath.includes(authUrl),
    );

    if (isOnAuthPage) {
      const qs = new URLSearchParams(window.location.search);
      const redirect_url = qs.get("redirect_url");
      const signinRedirectUrl: string =
        redirect_url || this.env.signinRedirectUrl || "/";
      window.location.href = signinRedirectUrl;
    }
  }

  private refreshAuthState() {
    this.auth = { isAuthenticated: Auth.isAuthenticated() };
  }
}
